import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import {
  Sun, Moon, MoonStars, ForkKnife, Television, MagicWand,
  CaretDown, CheckCircle, Sparkle, Clock,
} from '@phosphor-icons/react';

/**
 * Compact theme picker dropdown — fits into the page header.
 * Shows current effective mode + auto-detection reason; lets user pick
 * Auto / Light / Dark / Dim / Bedtime / Kitchen.
 */
const MODE_META = {
  auto:    { label: 'Auto',    icon: MagicWand, color: '#A855F7', tagline: 'Smart context' },
  light:   { label: 'Light',   icon: Sun,       color: '#F59E0B', tagline: 'Bright & airy' },
  dark:    { label: 'Dark',    icon: Moon,      color: '#FF6B35', tagline: 'Standard dark' },
  dim:     { label: 'Dim',     icon: Television, color: '#6B7280', tagline: 'TV ambient' },
  bedtime: { label: 'Bedtime', icon: MoonStars, color: '#EF4444', tagline: 'Red, melatonin-safe' },
  kitchen: { label: 'Kitchen', icon: ForkKnife, color: '#16A34A', tagline: 'High-contrast bright' },
};

const REASON_LABEL = {
  'kiosk-display':    'Kiosk display',
  'phone-night':      'Phone · night',
  'tablet-cooking':   'Tablet · cooking time',
  'daytime':          'Daytime',
  'evening':          'Evening',
  'manual':           'Manual',
};

export default function ThemeSwitcher({ compact = false, direction = 'down' }) {
  const { mode, preference, setMode, isAuto, reason, sunrise, sunset } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const meta = MODE_META[mode] || MODE_META.dark;
  const Icon = meta.icon;
  const isUp = direction === 'up';
  const menuPos = isUp
    ? 'absolute left-0 bottom-full mb-2 w-72 rounded-2xl shadow-2xl z-50 overflow-hidden'
    : 'absolute right-0 mt-2 w-72 rounded-2xl shadow-2xl z-50 overflow-hidden';

  return (
    <div className="relative" ref={ref} data-testid="theme-switcher">
      <button onClick={() => setOpen(o => !o)}
        data-testid="theme-switcher-toggle"
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition-all hover:scale-[1.02]"
        style={{
          background: `${meta.color}15`,
          borderColor: `${meta.color}40`,
          color: meta.color,
        }}
        title={`${meta.label}${isAuto ? ' (auto: ' + (REASON_LABEL[reason] || reason) + ')' : ''}`}>
        <Icon size={14} weight="duotone" />
        {!compact && <span className="text-[11px] font-semibold uppercase tracking-wider">{meta.label}</span>}
        {!compact && <CaretDown size={10} weight="bold" />}
      </button>

      {open && (
        <div className={menuPos}
          style={{
            background: 'rgba(20,20,20,0.97)',
            border: '1px solid rgba(255,255,255,0.1)',
            backdropFilter: 'blur(20px)',
          }}
          data-testid="theme-switcher-menu">
          <div className="p-3 border-b border-white/5">
            <div className="flex items-center gap-2 mb-1">
              <Sparkle size={11} weight="fill" style={{ color: meta.color }} />
              <p className="text-[10px] font-bold uppercase tracking-wider text-white">Active theme</p>
            </div>
            <p className="text-sm font-bold text-white flex items-center gap-1.5">
              <Icon size={14} weight="duotone" style={{ color: meta.color }} /> {meta.label}
            </p>
            {isAuto && reason && (
              <p className="text-[10px] text-neutral-400 mt-1 flex items-center gap-1">
                <Clock size={9} /> Auto · {REASON_LABEL[reason] || reason}
              </p>
            )}
            {sunrise && sunset && (
              <p className="text-[9px] text-neutral-500 mt-0.5">
                Today: ☀ {fmtTime(sunrise)} → 🌙 {fmtTime(sunset)}
              </p>
            )}
          </div>

          <div className="p-1.5 space-y-0.5 max-h-[280px] overflow-y-auto">
            <ModeOption modeKey="auto" current={preference} onSelect={(m) => { setMode(m); setOpen(false); }} />
            <div className="h-px bg-white/5 my-1" />
            {['light', 'dark', 'dim', 'bedtime', 'kitchen'].map(m => (
              <ModeOption key={m} modeKey={m} current={preference} onSelect={(x) => { setMode(x); setOpen(false); }} active={m === mode} />
            ))}
          </div>

          <div className="px-3 py-2 border-t border-white/5 text-[10px] text-neutral-500 leading-relaxed">
            <strong className="text-neutral-300">Auto</strong> picks the best mode based on device, time of day, and your home's sunrise/sunset.
          </div>
        </div>
      )}
    </div>
  );
}

function ModeOption({ modeKey, current, onSelect, active }) {
  const meta = MODE_META[modeKey];
  if (!meta) return null;
  const Icon = meta.icon;
  const selected = current === modeKey;
  return (
    <button onClick={() => onSelect(modeKey)} data-testid={`theme-option-${modeKey}`}
      className="w-full flex items-center gap-3 px-2.5 py-2 rounded-xl transition-colors hover:bg-white/5"
      style={{ background: selected ? `${meta.color}12` : 'transparent' }}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${meta.color}20`, color: meta.color }}>
        <Icon size={16} weight="duotone" />
      </div>
      <div className="flex-1 text-left min-w-0">
        <p className="text-xs font-bold text-white">{meta.label}</p>
        <p className="text-[10px] text-neutral-500 truncate">{meta.tagline}</p>
      </div>
      {selected && <CheckCircle size={14} weight="fill" style={{ color: meta.color }} />}
      {!selected && active && (
        <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
          style={{ background: `${meta.color}15`, color: meta.color }}>Active</span>
      )}
    </button>
  );
}

function fmtTime(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}
